// Global Instructions Rule Applied!

import {
  SET_FUND_TRANSACTIONS,
  APPEND_FUND_TRANSACTIONS,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  UPDATE_FUND_TRANSACTION_RECORD,
  REMOVE_FUND_TRANSACTION
} from './constants.ts'
import { FundTransactionRecord, FundTransactionRecordFields } from '../../data_models/fund_transaction_record.ts'
import { FilterValue, SortConfig } from '../../../app_modules/global_configs/tableViewPreferences.ts'

export interface FundTransactionsPaginatedResponse {
  data: FundTransactionRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface FundTransactionsState {
  data: FundTransactionRecord[]
  activeFilters: FilterValue[]
  activeSorting: SortConfig[]
  hasMore: boolean
  currentPage: number
}

type FundTransactionsAction =
  | { type: typeof SET_FUND_TRANSACTIONS; payload: { response: FundTransactionsPaginatedResponse } }
  | { type: typeof APPEND_FUND_TRANSACTIONS; payload: { response: FundTransactionsPaginatedResponse } }
  | { type: typeof SET_ACTIVE_FILTERS; payload: { filters: FilterValue[] } }
  | { type: typeof SET_ACTIVE_SORTING; payload: { sorting: SortConfig[] } }
  | { type: typeof CLEAR_ACTIVE_FILTERS }
  | { type: typeof UPDATE_FUND_TRANSACTION_RECORD; payload: { transactionId: string; updates: Record<string, any> } }
  | { type: typeof REMOVE_FUND_TRANSACTION; payload: { transactionId: string } }

const fundTransactionsReducer = (
  state: FundTransactionsState = {
    data: [],
    activeFilters: [],
    activeSorting: [],
    hasMore: false,
    currentPage: 1
  },
  action: FundTransactionsAction
): FundTransactionsState => {
  const idKey = FundTransactionRecordFields.fund_transactions__id
  switch (action.type) {
    case SET_FUND_TRANSACTIONS: {
      const { response } = action.payload
      return { ...state, data: response.data, hasMore: response.hasMore, currentPage: 1 }
    }

    case APPEND_FUND_TRANSACTIONS: {
      const { response } = action.payload
      return {
        ...state,
        data: [...state.data, ...response.data],
        hasMore: response.hasMore,
        currentPage: state.currentPage + 1
      }
    }

    case SET_ACTIVE_FILTERS: {
      return { ...state, activeFilters: action.payload.filters }
    }

    case SET_ACTIVE_SORTING: {
      return { ...state, activeSorting: action.payload.sorting }
    }

    case CLEAR_ACTIVE_FILTERS: {
      return { ...state, activeFilters: [] }
    }

    case UPDATE_FUND_TRANSACTION_RECORD: {
      const { transactionId, updates } = action.payload
      if (!state.data.some((r) => (r as any)[idKey] === transactionId)) return state
      return {
        ...state,
        data: state.data.map((r) => ((r as any)[idKey] === transactionId ? { ...r, ...updates } : r))
      }
    }

    case REMOVE_FUND_TRANSACTION: {
      const { transactionId } = action.payload
      return { ...state, data: state.data.filter((r) => (r as any)[idKey] !== transactionId) }
    }

    default:
      return state
  }
}

export default fundTransactionsReducer

export const setFundTransactions = (payload: { response: FundTransactionsPaginatedResponse }) => ({
  type: SET_FUND_TRANSACTIONS,
  payload
})

export const appendFundTransactions = (payload: { response: FundTransactionsPaginatedResponse }) => ({
  type: APPEND_FUND_TRANSACTIONS,
  payload
})

export const setFundTransactionsActiveFilters = (payload: { filters: FilterValue[] }) => ({
  type: SET_ACTIVE_FILTERS,
  payload
})

export const setFundTransactionsActiveSorting = (payload: { sorting: SortConfig[] }) => ({
  type: SET_ACTIVE_SORTING,
  payload
})

export const clearFundTransactionsActiveFilters = () => ({ type: CLEAR_ACTIVE_FILTERS })

export const updateFundTransactionRecord = (payload: { transactionId: string; updates: Record<string, any> }) => ({
  type: UPDATE_FUND_TRANSACTION_RECORD,
  payload
})

export const removeFundTransaction = (payload: { transactionId: string }) => ({
  type: REMOVE_FUND_TRANSACTION,
  payload
})

export const selectFundTransactionById = (state: any, transactionId: string) =>
  (state.fundTransactions?.data ?? []).find(
    (r: any) => r[FundTransactionRecordFields.fund_transactions__id] === transactionId
  )
